import React, { useEffect, useRef, useState } from 'react';
import { Bar } from '@antv/g2plot';
import styles from './styles/receivables-aging.module.scss';

const ReceivablesAgingChart = ({ data }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(550);
  const [containerHeight, setContainerHeight] = useState(320);

  // Transform backend receivables aging data
  // Data format: { current, '0-30', '31-60', '61-90', overdue, fundsAdvanced }
  const transformData = (rawData) => {
    if (!rawData || typeof rawData !== 'object') {
      return [];
    }
    
    // Map aging buckets to display order
    const buckets = [
      { name: 'Current', key: 'current' },
      { name: '0-30', key: '0-30' },
      { name: '31-60', key: '31-60' },
      { name: '61-90', key: '61-90' },
      { name: '91-120', key: 'overdue' }, // overdue maps to 91-120
    ];

    return buckets.map(bucket => ({
      category: bucket.name,
      value: rawData[bucket.key] || 0,
    })).filter(item => item.value > 0); // Only show buckets with values
  };

  useEffect(() => {
    // Function to get container width - observe the wrapper (card) that's in the grid
    const updateWidth = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        const width = rect.width || wrapperRef.current.offsetWidth || 550;
        setContainerWidth(Math.max(width, 300));
      }
    };

    // Initial width calculation with a small delay to ensure DOM is ready
    const timeout = setTimeout(updateWidth, 100);

    // Use ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        const width = entry.contentRect.width || entry.target.offsetWidth || 550;
        setContainerWidth(Math.max(width, 300));
      }
    });

    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }

    // Poll during sidebar transitions using requestAnimationFrame
    let rafId = null;
    let lastWidth = 0;
    const pollWidth = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        const currentWidth = rect.width || wrapperRef.current.offsetWidth || 550;
        if (Math.abs(currentWidth - lastWidth) > 1) {
          setContainerWidth(Math.max(currentWidth, 300));
          lastWidth = currentWidth;
        }
        rafId = requestAnimationFrame(pollWidth);
      }
    };

    // Start polling
    rafId = requestAnimationFrame(pollWidth);

    // Also listen to window resize as fallback
    window.addEventListener('resize', updateWidth);

    // Watch for sidebar class changes (for transition detection)
    const sidebarObserver = new MutationObserver(() => {
      updateWidth();
    });

    // Find sidebar element
    const sidebarElement = document.querySelector('[class*="sidebar"]');
    if (sidebarElement) {
      sidebarObserver.observe(sidebarElement, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    return () => {
      clearTimeout(timeout);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
      sidebarObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  useEffect(() => {
    // Transform backend data
    const chartData = transformData(data || {});

    if (chartRef.current && containerWidth > 0 && chartData.length > 0) {
      // If chart instance exists, update it instead of recreating
      if (chartInstanceRef.current) {
        chartInstanceRef.current.update({
          data: chartData,
          width: containerWidth,
          height: containerHeight,
        });
      } else {
        // Create new horizontal bar chart instance (single series, no grouping)
        chartInstanceRef.current = new Bar(chartRef.current, {
          data: chartData,
          xField: 'value',
          yField: 'category',
          color: '#3b82f6', // Blue bars
          tooltip: {
            formatter: (datum) => {
              return { name: 'Receivables', value: datum.value ? datum.value.toLocaleString() : '0' };
            },
          },
          label: {
            position: 'right',
            formatter: (datum) => {
              // Don't show label if value is 0
              if (!datum.value || datum.value === 0) {
                return '';
              }
              return datum.value.toLocaleString();
            },
            style: {
              fontSize: 10,
              fill: '#ffffff',
              fontWeight: 500,
            },
          },
          xAxis: {
            min: 0,
            grid: {
              line: {
                style: {
                  stroke: '#e5e7eb',
                  lineWidth: 1,
                },
              },
            },
          },
          yAxis: {
            label: {
              style: {
                fill: '#6b7280',
                fontSize: 11,
                fontFamily: 'Inter, SF Pro Text, sans-serif',
              },
            },
            line: null,
            tickLine: null,
          },
          height: containerHeight,
          width: containerWidth,
        });

        chartInstanceRef.current.render();
      }
    }

    // Cleanup function
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [data, containerWidth, containerHeight]);

  return (
    <div className={styles.card} ref={wrapperRef}>
      <div className={styles.header}>
        <h3 className={styles.title}>Aging Receivables Summary</h3>
      </div>
      
      <div className={styles.chartContainer} ref={containerRef}>
        <div ref={chartRef} style={{ width: '100%', height: `${containerHeight}px` }} />
      </div>
    </div>
  );
};

export default ReceivablesAgingChart;
